import React, { useState, useEffect, useCallback } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip,
} from "recharts";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Card, Button, Input, Modal, Spinner } from "./ui";
import { formatMoney } from "../lib/utils";
import { RANGES, accountTypeLabel, activityTypeLabel, activityColor } from "../lib/portfolio";
import { ActivityModal } from "./ActivityLedger";
import ImportActivitiesModal from "./ImportActivitiesModal";
import { HoldingMeta, ClassBadge, ExpiryBadge } from "./HoldingMeta";
import { ArrowLeft, Pencil, Trash2, Plus, Check, X, TrendingUp, TrendingDown, Link2, Upload } from "lucide-react";

const POS = "#10B981";
const NEG = "#EF4444";

function Stat({ label, value, color, testid }) {
  return (
    <div className="rounded-xl border px-3.5 py-3" data-testid={testid}>
      <div className="text-[11px] text-text-secondary mb-0.5">{label}</div>
      <div className="text-base font-semibold tabular-nums" style={color ? { color } : {}}>{value}</div>
    </div>
  );
}

// Name + copy-link + account type + total value + period performance.
function AccountHeader({ t, lang, entity, acc, data, perf, base, range, hidden, money, lineColor, up, copyLink, copied }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2.5">
        <h2 className="text-2xl font-heading font-semibold">{acc.name}</h2>
        <button onClick={copyLink} title={t("portfolio_copy_link")} data-testid="account-copy-link"
          className="text-text-secondary hover:text-text-primary transition-colors">
          {copied ? <Check className="w-4 h-4" style={{ color: POS }} /> : <Link2 className="w-4 h-4" />}
        </button>
      </div>
      <p className="text-xs text-text-secondary">{accountTypeLabel(acc.type, acc.custom_label, entity.country, lang)}</p>
      <div className="mt-2 text-4xl font-heading font-semibold tracking-tight tabular-nums" data-testid="account-total-value">{money(data.total_value, 2)}</div>
      {perf && (
        <div className="mt-1 text-sm" style={{ color: lineColor }}>
          {hidden ? "••••" : `${up ? "+" : "−"}${formatMoney(Math.abs(perf.change || 0), base, { decimals: 2 }).replace(/^-/, "")}`}
          {perf.change_pct !== null && perf.change_pct !== undefined && <span className="ml-2 opacity-80">{up ? "+" : ""}{perf.change_pct}%</span>}
          <span className="ml-2 text-text-secondary">{t("portfolio_past")} {range}</span>
        </div>
      )}
    </div>
  );
}

// Value-over-time area chart + range selector pills.
function AccountChart({ t, perf, lineColor, base, hidden, range, setRange }) {
  return (
    <>
      <Card className="!p-3 mb-3">
        <div style={{ width: "100%", height: 220 }} data-testid="account-chart">
          {perf?.series?.length > 1 ? (
            <ResponsiveContainer>
              <AreaChart data={perf.series} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="accFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={lineColor} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={lineColor} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" hide />
                <YAxis domain={["auto", "auto"]} hide />
                <Tooltip
                  contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, fontSize: 12 }}
                  labelStyle={{ color: "var(--text-secondary)" }}
                  formatter={(v) => [hidden ? "••••" : formatMoney(v, base, { decimals: 0 }), t("portfolio_total")]} />
                <Area type="monotone" dataKey="value" stroke={lineColor} strokeWidth={2} fill="url(#accFill)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-sm text-text-secondary">{t("portfolio_no_holdings")}</div>
          )}
        </div>
      </Card>
      <div className="flex items-center justify-center gap-1 mb-6" data-testid="account-ranges">
        {RANGES.map((r) => (
          <button key={r} onClick={() => setRange(r)} data-testid={`account-range-${r}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${range === r ? "bg-text-primary text-background" : "text-text-secondary hover:text-text-primary"}`}>{r}</button>
        ))}
      </div>
    </>
  );
}

// Cash (inline-editable) + investments / net contribution / cost basis cards.
function AccountStats({ t, canEdit, editCash, setEditCash, cashInput, setCashInput, acc, saveCash, money, data }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
      <div className="rounded-xl border px-3.5 py-3" data-testid="account-cash-card">
        <div className="text-[11px] text-text-secondary mb-0.5 flex items-center justify-between">
          {t("portfolio_cash")}
          {canEdit && !editCash && (
            <button onClick={() => { setEditCash(true); setCashInput(String(acc.cash ?? 0)); }} data-testid="account-cash-edit" className="text-text-secondary hover:text-text-primary"><Pencil className="w-3 h-3" /></button>
          )}
        </div>
        {editCash ? (
          <div className="flex items-center gap-1">
            <Input type="number" value={cashInput} onChange={(e) => setCashInput(e.target.value)} className="h-7 text-sm" data-testid="account-cash-field" />
            <button onClick={saveCash} data-testid="account-cash-save" className="text-green-500"><Check className="w-4 h-4" /></button>
            <button onClick={() => setEditCash(false)} className="text-text-secondary"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <div className="text-base font-semibold tabular-nums">{money(data.cash_balance, 2)}</div>
        )}
      </div>
      <Stat label={t("portfolio_investments")} value={money(data.investments, 2)} testid="account-investments" />
      <Stat label={t("portfolio_net_contribution")} value={money(data.net_contribution, 2)} testid="account-net-contribution" />
      <Stat label={t("portfolio_cost_basis")} value={money(data.cost_basis, 2)} testid="account-cost-basis" />
    </div>
  );
}

// Positions table with live price / value / unrealized gain.
function HoldingsTable({ t, data, money, base, hidden }) {
  return (
    <Card className="mb-5" data-testid="account-holdings">
      <h3 className="font-heading font-semibold mb-3">{t("portfolio_holdings")}</h3>
      {data.holdings.length === 0 ? (
        <p className="text-sm text-text-secondary py-3">{t("portfolio_no_holdings")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] text-text-secondary text-left border-b">
                <th className="py-2 font-medium">{t("portfolio_position")}</th>
                <th className="py-2 font-medium text-right">{t("portfolio_quantity")}</th>
                <th className="py-2 font-medium text-right">{t("portfolio_today_price")}</th>
                <th className="py-2 font-medium text-right">{t("portfolio_total")}</th>
                <th className="py-2 font-medium text-right">{t("portfolio_unrealized_gain")}</th>
              </tr>
            </thead>
            <tbody>
              {data.holdings.map((h) => (
                <tr key={h.id} className="border-b last:border-0" data-testid={`account-holding-${h.id}`}>
                  <td className="py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="font-medium">{h.name}</div>
                      <ClassBadge type={h.type} />
                      <ExpiryBadge holding={h} />
                    </div>
                    {h.symbol && !h.held_at_cost && <div className="text-[11px] text-text-secondary font-mono">{h.symbol}</div>}
                    <HoldingMeta holding={h} />
                  </td>
                  <td className="py-2.5 text-right tabular-nums">{h.quantity}</td>
                  <td className="py-2.5 text-right tabular-nums">{h.price ? formatMoney(h.price, h.currency, { decimals: 2 }) : "—"}</td>
                  <td className="py-2.5 text-right tabular-nums font-medium">{money(h.value, 2)}</td>
                  <td className="py-2.5 text-right tabular-nums" style={{ color: (h.gain ?? 0) >= 0 ? POS : NEG }}>
                    {h.gain === null || h.gain === undefined ? "—" : (
                      <span className="inline-flex items-center gap-1 justify-end">
                        {(h.gain >= 0) ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {hidden ? "••••" : `${h.gain >= 0 ? "+" : ""}${formatMoney(h.gain, base, { decimals: 0 })}`}
                        {h.gain_pct !== null && h.gain_pct !== undefined && ` (${h.gain_pct >= 0 ? "+" : ""}${h.gain_pct}%)`}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// Account activity ledger (with add / import / edit / delete affordances).
function ActivitiesList({ t, lang, data, canEdit, acc, onImport, onAdd, onEdit, onDelete }) {
  return (
    <Card data-testid="account-activities">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading font-semibold">{t("activity_title")} <span className="text-xs text-text-secondary">{data.activities.length}</span></h3>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onImport} data-testid="account-import-activity">
              <Upload className="w-4 h-4 mr-1" /> {t("import_csv")}
            </Button>
            <Button size="sm" onClick={onAdd} data-testid="account-add-activity">
              <Plus className="w-4 h-4 mr-1" /> {t("activity_add")}
            </Button>
          </div>
        )}
      </div>
      {data.activities.length === 0 ? (
        <p className="text-sm text-text-secondary py-3">{t("activity_none")}</p>
      ) : (
        <div className="space-y-1.5">
          {data.activities.map((a) => (
            <div key={a.id} className="rounded-lg border px-3 py-2 flex items-center gap-3 text-sm group" data-testid={`account-activity-${a.id}`}>
              <div className="w-20 shrink-0 text-text-secondary text-xs tabular-nums">{a.date}</div>
              <span className="shrink-0 px-2 py-0.5 rounded-md text-xs font-semibold"
                style={{ color: activityColor(a.type), background: `${activityColor(a.type)}1f` }}
                data-testid={`account-activity-type-${a.id}`}>
                {activityTypeLabel(a.type, lang)}
              </span>
              <div className="min-w-0 flex-1 truncate text-text-secondary">
                {a.symbol && <span className="font-mono text-text-primary mr-1.5">{a.symbol}</span>}
                {a.quantity ? `${a.quantity}${a.price ? ` × ${formatMoney(a.price, a.currency || acc.currency, { decimals: 2 })}` : ""}` : ""}
              </div>
              {a.amount ? <div className="text-right tabular-nums shrink-0">{formatMoney(a.amount, a.currency || acc.currency, { decimals: 2 })}</div> : null}
              {canEdit && (
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => onEdit(a)} data-testid={`account-activity-edit-${a.id}`} className="text-text-secondary hover:text-text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onDelete(a)} data-testid={`account-activity-delete-${a.id}`} className="text-text-secondary hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function AccountDetail({ entity, accountId, accounts, hidden, onBack, onChanged }) {
  const { t, lang, canEdit, toast } = useApp();
  const [copied, setCopied] = useState(false);
  const [data, setData] = useState(null);
  const [range, setRange] = useState("3M");
  const [loading, setLoading] = useState(true);
  const [editCash, setEditCash] = useState(false);
  const [cashInput, setCashInput] = useState("");
  const [activityModal, setActivityModal] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [pendingDelActivity, setPendingDelActivity] = useState(null);

  const base = data?.base_currency || "EUR";
  const money = (v, dec = 0) => (hidden ? "••••" : formatMoney(v, base, { decimals: dec }));

  const copyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}?tab=holdings&account=${accountId}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied(true);
    toast(t("portfolio_link_copied"));
    setTimeout(() => setCopied(false), 1500);
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/portfolio/account-summary?account_id=${accountId}&range=${range}`);
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [accountId, range]);

  useEffect(() => { load(); }, [load]);

  const saveCash = async () => {
    await api.put(`/portfolio/accounts/${accountId}`, { cash: Number(cashInput) || 0 });
    setEditCash(false);
    load();
    onChanged?.();
  };

  const reloadAll = () => { load(); onChanged?.(); };

  const doDeleteActivity = async () => {
    if (!pendingDelActivity) return;
    const id = pendingDelActivity.id;
    setPendingDelActivity(null);
    try {
      await api.delete(`/portfolio/activities/${id}`);
    } catch (e) {
      if (e?.response?.status !== 404) { return; }
    }
    reloadAll();
  };

  if (loading || !data) {
    return (
      <div data-testid="account-detail">
        <button onClick={onBack} className="text-sm text-text-secondary hover:text-text-primary inline-flex items-center gap-1 mb-4" data-testid="account-back">
          <ArrowLeft className="w-4 h-4" /> {t("portfolio_back")}
        </button>
        <div className="flex justify-center py-16"><Spinner className="w-6 h-6 text-text-secondary" /></div>
      </div>
    );
  }

  const acc = data.account;
  const perf = data.performance;
  const up = (perf?.change ?? 0) >= 0;
  const lineColor = up ? POS : NEG;

  return (
    <div data-testid="account-detail">
      <button onClick={onBack} className="text-sm text-text-secondary hover:text-text-primary inline-flex items-center gap-1 mb-4" data-testid="account-back">
        <ArrowLeft className="w-4 h-4" /> {t("portfolio_back")}
      </button>

      <AccountHeader t={t} lang={lang} entity={entity} acc={acc} data={data} perf={perf}
        base={base} range={range} hidden={hidden} money={money} lineColor={lineColor} up={up}
        copyLink={copyLink} copied={copied} />

      <AccountChart t={t} perf={perf} lineColor={lineColor} base={base} hidden={hidden}
        range={range} setRange={setRange} />

      <AccountStats t={t} canEdit={canEdit} editCash={editCash} setEditCash={setEditCash}
        cashInput={cashInput} setCashInput={setCashInput} acc={acc} saveCash={saveCash}
        money={money} data={data} />

      <HoldingsTable t={t} data={data} money={money} base={base} hidden={hidden} />

      <ActivitiesList t={t} lang={lang} data={data} canEdit={canEdit} acc={acc}
        onImport={() => setImportOpen(true)} onAdd={() => setActivityModal({})}
        onEdit={(a) => setActivityModal(a)} onDelete={(a) => setPendingDelActivity(a)} />

      {activityModal && (
        <ActivityModal entity={entity} accounts={accounts} defaultAccountId={accountId}
          activity={activityModal.id ? activityModal : null}
          onClose={() => setActivityModal(null)}
          onSaved={() => { setActivityModal(null); reloadAll(); }} />
      )}
      {importOpen && (
        <ImportActivitiesModal entity={entity} accounts={accounts} defaultAccountId={accountId}
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); reloadAll(); }} />
      )}
      {pendingDelActivity && (
        <Modal open onClose={() => setPendingDelActivity(null)} title={t("delete")}
          footer={<>
            <Button variant="outline" onClick={() => setPendingDelActivity(null)} data-testid="account-activity-delete-cancel">{t("cancel")}</Button>
            <Button onClick={doDeleteActivity} className="!bg-red-500 hover:!bg-red-600" data-testid="account-activity-delete-confirm">{t("delete")}</Button>
          </>}>
          <p className="text-sm text-text-secondary">{t("activity_delete_confirm")}</p>
        </Modal>
      )}
    </div>
  );
}
